/**
 * The Hand Studio panel's decisions, headless (the ADR-015 §9 idiom — a value
 * module beside a DOM shell, so the rules are testable without a browser).
 *
 * THE SHAPE CHANGED WITH ADR-022 T3, and it is worth saying what it was. This
 * held `digits: Record<clusterId, DigitChoice>` — a person walked the analyzer's
 * candidate rows and answered each one, including an explicit "not a digit" for
 * every wrist artifact the scan turned up. The answers were good; the QUESTION
 * was the analyzer's. What a person actually knows is *that is the index
 * finger, right there*, so the state is now a set of PINS: places on the model
 * they pointed at, each carrying a name.
 *
 * Everything that followed from the old shape is gone with it (§6):
 *
 *  - `assignDigit` / `assignedDigits` / `withAutoSetAside` — pin editing lives
 *    in `hands/digit-pins`, which owns the one-digit-one-place rule.
 *  - `resolveAssignments` / `resolutionsCoveringAll` — the covering-resolution
 *    hunt, deleted with the question in T2.
 *  - `pickThumb` / `thumbBranchId` — the thumb is a pin like any other now; the
 *    analyzer's thumb reading is a PRE-FILL anchor (D4), not a state a person
 *    has to set before anything else works.
 *  - `handProfile` / `declareProfile` / `HAND_PROFILE_CHOICES` — the count
 *    question left the surface when every digit became individually named
 *    (there is nothing left to cross-check), and the state outlived the control
 *    by one arc. The Document vocabulary keeps `handProfile` for Documents that
 *    were written with it; nothing in this app asks for it any more.
 *
 * What did NOT change:
 *
 *  - NOTHING IS PRE-ANSWERED into a graft. Pre-fill proposes NAMES for places
 *    the volume already found (D4, and always re-assignable) — it never places
 *    a pin. `canGraft` is false until a person has pinned and named at least
 *    one digit, and the pin is the only thing that can put a digit on a hand.
 *  - A HAND THAT ALREADY HAS FINGERS IS NOT A CANDIDATE. The kernel refuses the
 *    ontology-id collision, so the surface must not spend a person's attention
 *    on a pin that cannot land.
 *
 * Depends on: hands/hand-candidates + hands/digit-pins (types + pin rules).
 * Depended on by: hands-panel.
 */
import { offeredDigits } from './digit-flyout.js';
import type { HandCandidate } from '../hands/hand-candidates.js';
import { emptyPinSet, namedPins, unpinnedClusters, type PinSet } from '../hands/digit-pins.js';
import type { TipConsensus } from '../hands/tip-consensus.js';

export interface HandUiState {
  readonly resolution: number;
  /**
   * Recenter the wrist bind into the wrist volume with the graft
   * (unbind-adjust-rebind #1). ON by default — REVERSED 2026-08-02 by
   * Allen's foxDude field report: the specimen's R wrist is authored
   * 33–45 mm anterior of the wrist volume (L is centered — generated rigs
   * are not bilaterally symmetric), the quiet opt-in sailed it through, and
   * his read was "no snap to the middle was made". The cure is measured,
   * mesh-immobile, and undoable, so the proposal now arrives pre-filled;
   * the person unchecks to decline, and the measured mm stays visible
   * either way. (§8 is intact: the human still confirms — the Graft click
   * signs the whole package with the recenter line in view.)
   */
  readonly recenterWrist: boolean;
  /**
   * WHERE THE WRIST IS (ADR-025) — a BIND-WORLD point the person clicked on
   * the model, or null. Tripo drops the hand joint into the palm on some
   * characters and everything downstream anchors to it; the click moves the
   * joint ALONG the arm to this station (the lateral seat stays the recentre's)
   * and re-owns the palm root. Allen's order is wrist → tips → knuckles, so
   * the flyout offers this FIRST on a hand that has none. Null = the joint
   * stands, bit-identical (D4).
   */
  readonly wrist: { readonly x: number; readonly y: number; readonly z: number } | null;
  /**
   * THE THUMB'S OPPOSITION, degrees (field 2026-08-15): how far the thumb's
   * dorsal is rotated from the fingers' toward the radial edge. Per RIG, not a
   * constant — a cupped, wrist-flexed bind (Sergio) is already partly opposed
   * and over-rotates at 90; a flat bind (Kara) takes 90. Dialled while watching
   * the clip; a measured default is the follow-up. Default 90.
   */
  readonly thumbOpposition: number;
  /** The places this person pointed at, and what they called them (ADR-022 §3). */
  readonly pins: PinSet;
  /**
   * How many digits this hand is believed to have — the flyout's ± value, and
   * the thing that decides when naming is FINISHED (and therefore when the
   * graft can run itself, XD §5.6 gate 4).
   *
   * Five by default because five is the common case and being wrong is one
   * click. It is a BELIEF, not a measurement: the volume's own count is what
   * `hand-partial` says on a hairball, which is why nothing here reads it.
   */
  readonly expected: number;
  /**
   * Is the next click on the MODEL a pin? Armed per hand and explicitly, so a
   * click in the viewport never silently means something new — the person turns
   * the gesture on, the cursor becomes a crosshair, and the pose gizmo steps
   * aside for as long as it is on (ADR-006 §13.2, one meaning per click).
   */
  readonly arming: boolean;
}

/**
 * Where the knob starts. NOT a measured band — no such thing exists across
 * specimens (the android wanted 38 on one hand and had no good answer on the
 * other), so this is an opening position for a person to move, and the
 * resolution it produces is recorded with provenance 'declared' only once they
 * have moved through it and grafted.
 */
export const DEFAULT_RESOLUTION = 40;

/**
 * How many digits a hand is assumed to have until a person says otherwise.
 * Five, because five is the common case and the correction costs one click —
 * and because being told "this one has four" is a thing only the person who
 * made the character can say (§8).
 */
export const DEFAULT_EXPECTED_DIGITS = 5;

/** Anatomical opposition — the kernel's default too (`THUMB_OPPOSITION_DEG_DEFAULT`). */
export const DEFAULT_THUMB_OPPOSITION = 90;

export function initialHandUi(): HandUiState {
  return {
    resolution: DEFAULT_RESOLUTION,
    recenterWrist: true,
    wrist: null,
    thumbOpposition: DEFAULT_THUMB_OPPOSITION,
    pins: emptyPinSet,
    arming: false,
    expected: DEFAULT_EXPECTED_DIGITS,
  };
}

/** Set the believed digit count, clamped to the vocabulary (1–5). */
export function setExpected(state: HandUiState, expected: number): HandUiState {
  return { ...state, expected: Math.max(1, Math.min(5, Math.round(expected))) };
}

/**
 * Naming is FINISHED when the digits this hand HAS are exactly the digits that
 * have been named. This is the condition the graft runs itself on — XD §5.6
 * gate 4: when the engine can settle it honestly on its own, it acts and SAYS
 * SO with a way back. Every name in that set came from a trusted human click,
 * so there is nothing left for a confirm button to add.
 *
 * IT IS A SET MATCH AND NOT A COUNT (Codex P1, #94). Counting was enough right
 * up until the ± existed: name Thumb · Index · Middle, then press − to say the
 * hand has three digits, and three names against a count of three read as
 * finished — while the anatomy of a three-digit hand is Thumb · Index ·
 * **Pinky**, because the ulnar-most finger is always the Pinky. The graft would
 * have run with a name that hand does not have, and ring motion would have
 * driven an outer finger. `reconcileNames` un-names the casualties when the
 * count changes; this makes the wrong state unreachable rather than unlikely.
 */
export function namingComplete(state: HandUiState): boolean {
  const offered = offeredDigits(state.expected);
  const named = new Set(namedPins(state.pins).map((p) => p.digit));
  return named.size === offered.length && offered.every((d) => named.has(d));
}

/** Dial the thumb opposition, clamped to the schema's band (−180…180). */
export function setThumbOpposition(state: HandUiState, deg: number): HandUiState {
  return { ...state, thumbOpposition: Math.max(-180, Math.min(180, Math.round(deg))) };
}

/** Place, move or clear the wrist station (ADR-025) — the person's click, never a proposal. */
export function setWrist(state: HandUiState, wrist: HandUiState['wrist']): HandUiState {
  return { ...state, wrist };
}

/**
 * THE NUDGE (ADR-025 D3, Allen): a hand named complete without a wrist answer
 * is not refused — good-wrist characters do not need one — but it is worth a
 * quiet word, because a joint that overshoots into the palm is the one thing
 * that makes every other answer on this hand land wrong.
 */
export function wristNudge(state: HandUiState): boolean {
  return state.wrist === null && namingComplete(state);
}

/** Flip the wrist-recenter proposal — a person's switch, kept across resolution moves. */
export function toggleRecenterWrist(state: HandUiState): HandUiState {
  return { ...state, recenterWrist: !state.recenterWrist };
}

/** Arm or disarm the pin gesture for this hand. */
export function setArming(state: HandUiState, arming: boolean): HandUiState {
  return { ...state, arming };
}

export function withPins(state: HandUiState, pins: PinSet): HandUiState {
  return { ...state, pins };
}

/**
 * Move the resolution. THE PINS SURVIVE — that is the whole point of ADR-022.
 * A pin is a world-space place a person pointed at; the slider governs only
 * what the volume can SHOW there. Under the old branch-keyed answers this had
 * to clear the thumb pin on every move, because `branch-1` at 38 and `branch-1`
 * at 56 are unrelated objects (Codex P2, #77) — that hazard is gone with the
 * key, not merely guarded.
 */
export function setResolution(state: HandUiState, resolution: number): HandUiState {
  if (resolution === state.resolution) return state;
  return { ...state, resolution };
}

/**
 * Ready when at least one pin carries a digit. That is the whole gate.
 *
 * TWO RULES DIED HERE (ADR-022 §6), and it is worth saying what they were for.
 *
 * COMPLETENESS ("every candidate answered, including 'not a digit'") existed so
 * an unnamed candidate could not mean both "not a digit" and "not yet looked
 * at". Under pins those are no longer the same state by construction: a pin is
 * a positive act at a place, so the absence of one IS the answer. Keeping the
 * rule would only have forced a person to formally decline every wrist artifact
 * the scan turned up. It survives as a NUDGE ({@link unpinnedStrongCandidates})
 * — visible advice that never disables the button.
 *
 * RESOLUTION COVERAGE ("every assigned digit separates HERE") is gone because
 * the question is gone: pins are placed from world positions and may come from
 * any resolution, or none. This is the rule that produced the dead end Allen
 * hit — four digits pinned, a fifth stubborn, and no move left but to delete
 * correct answers until the remainder happened to co-separate.
 */
export function canGraft(state: HandUiState): boolean {
  return namedPins(state.pins).length > 0;
}

/**
 * THE KNUCKLE WORKFLOW HOLDS THE DOOR (Allen, field 2026-08-14). The graft
 * fires itself when naming completes — his own ruling, and it stays — but the
 * fifth name used to fire it INSTANTLY, which made the fifth digit's knuckle
 * unpinnable: the graft ran between the last tip and the click that would have
 * placed its knuckle.
 *
 * A pinned knuckle IS the declaration "I am doing the two-row workflow", so it
 * is also the signal to wait: with one or more knuckles on the hand, completion
 * offers a Graft-now chip instead of firing, and the person grafts when their
 * rows are done. A hand with NO knuckles — the common case, the flat hands —
 * keeps the effortless instant graft exactly as ruled. No timer, no mode: the
 * person who uses the second row has told us, and nobody else pays for it.
 */
export function graftWaitsForKnuckles(state: HandUiState): boolean {
  return state.pins.pins.some((p) => p.knuckle !== null);
}

/**
 * THE HOLD, WIDENED (Codex P1, #97): in Allen's ruled order — wrist → tips →
 * knuckles — the fifth tip name arrives BEFORE the first knuckle exists, so a
 * hold that keys only on "a knuckle is present" fires the graft one click too
 * early and the first knuckle can never be placed. A WRIST answer is the
 * person saying "I am doing the guided workflow on this hand" — so it holds
 * the door too, and completion offers Graft-now. A hand with neither (the
 * flat, effortless case) keeps the instant graft exactly as ruled.
 */
export function graftHolds(state: HandUiState): boolean {
  return graftWaitsForKnuckles(state) || state.wrist !== null;
}

/**
 * Consensus candidates with strong evidence that nobody pinned — the nudge that
 * replaced the completeness gate. A person may graft straight past it; the point
 * is only that a real digit cannot go unnoticed, not that it must be formally
 * declined.
 *
 * "Strong" is the consensus detector's own reading: seen at more than one
 * resolution AND reaching distally. A one-resolution blip near the wrist is a
 * cuff rim, and nagging about it would teach people to ignore the nudge.
 */
export function unpinnedStrongCandidates(state: HandUiState, scan: TipConsensus): readonly string[] {
  return unpinnedClusters(state.pins, scan)
    .filter((c) => c.votes > 1 && c.reachFraction > 0.5)
    .map((c) => c.id);
}

/** What the surface should show for one hand, before any measurement is taken. */
export type HandDisposition =
  | { readonly kind: 'graftable' }
  | { readonly kind: 'already-fingered'; readonly children: readonly string[] };

export function dispositionOf(candidate: HandCandidate): HandDisposition {
  return candidate.existingChildren.length > 0
    ? { kind: 'already-fingered', children: candidate.existingChildren }
    : { kind: 'graftable' };
}

/**
 * The line describing a reading. The analyzer's classification appears — hiding
 * it would be its own kind of lie — but as an OPINION, beside the confidence
 * that qualifies it and pointing at the persistence figures that can contradict
 * it. On the android `hand-5` meant five real digits on one hand and three
 * digits plus two bumps on the other.
 */
export function readingSummary(input: {
  readonly branchCount: number;
  readonly profile: string;
  readonly confidence: string;
}): string {
  return (
    `${input.branchCount} branches · analyzer says "${input.profile}" at ${input.confidence} confidence — ` +
    `the pins below are the answer.`
  );
}

/** Why the graft button is unavailable, in the person's terms — never a bare disabled control. */
export function graftBlockedReason(state: HandUiState): string | null {
  if (namedPins(state.pins).length === 0) {
    return 'Point at a fingertip on the model and name it — thumb, index, middle, ring, or pinky. One is enough to graft.';
  }
  return null;
}
